import { useMemo, useState } from "react";
import { ScrollView, StyleSheet, Text, View, type LayoutChangeEvent } from "react-native";
import { useTranslation } from "react-i18next";
import {
  bandLabel,
  buildIntensityGridFromDraft,
  draftRoundTypes,
  loadStepFor,
  type CurrentMax,
  type Draft,
  type DraftGridLens,
  type DraftLineOptions,
  type UnitsPreference,
  dayLabel,
} from "@heraion/shared";

import { AddRoundChip } from "../IntensityGrid/GridAddChips";
import { useGridGestures } from "../IntensityGrid/useGridGestures";
import { Button } from "../primitives/Button";
import { ChipChoiceRow } from "../primitives/ChipChoiceRow";
import { IconButton } from "../primitives/IconButton";
import { useRoundTypeWords } from "../primitives/RoundTypeBadge";
import { ScaleLegend } from "../primitives/ScaleLegend";
import { useFinePointer } from "../primitives/useFinePointer";
import type { UiTheme } from "../theme";
import { GridRangeBar } from "./GridRangeBar";
import { groupLabels } from "./groupLabels";
import { LINE, ROW_MIN } from "./RoundGridCell";
import { HEAD_MIN, makeColumnStyles, RoundColumn, type ColumnCtx } from "./RoundColumn";
import { RoundMenu, type RoundMenuState } from "./RoundMenu";
import { ruleOfSlot } from "./ruleCache";
import { SelectToggle } from "../primitives/SelectToggle";
import { useGridEditing } from "./useGridEditing";
import type { GridWriteDeps } from "./useRoundCellWrites";
import type { V3DayActions } from "./useV3DayActions";
import type { V3Navigation } from "./useV3Navigation";
import type { V3Styles } from "./v3Styles";

const LENSES: readonly DraftGridLens[] = ["effort", "reps", "load", "sets"];
const NAME_W = 160;

// The day as a grid on a wide screen (#336 slice 6, brief §7.3, mock
// `wideView`). From 768 the heatmap lives here (§15 row 28): each cell takes
// its exercise's band in that round, by the heatmap's own rule, and the
// day's peak sits by its name. The rounds scroll sideways, the names stay
// put. Rows grow with the text: each reports its content's height, and a row
// of names and the same row of rounds take the tallest, so they line up.
// Part 2b: a block of cells is selected, copied, pasted and filled, and a cell
// is typed into in «Sets», «Reps» and «Load» (useGridEditing).
export function ExerciseRoundGrid({
  theme,
  styles,
  draft,
  nav,
  days,
  lineOptions,
  cellUnit,
  resolveName,
  maxMeta,
  onAddExercise,
  onDayMenu,
  writes,
}: {
  theme: UiTheme;
  styles: V3Styles;
  draft: Draft;
  nav: V3Navigation;
  days: V3DayActions;
  lineOptions: DraftLineOptions;
  cellUnit: UnitsPreference;
  resolveName: (id: string, snapshot?: string) => string;
  maxMeta?: ReadonlyMap<string, CurrentMax>;
  onAddExercise: () => void;
  /** The day menu's trigger, where no structure column carries it. */
  onDayMenu?: () => void;
  /** What the cells typed into and the block writes need of the editor. */
  writes: GridWriteDeps;
}) {
  const { t } = useTranslation();
  const g = useMemo(() => makeGridStyles(theme), [theme]);
  const c = useMemo(() => makeColumnStyles(theme), [theme]);
  const edit = useGridEditing({ draft, nav, lineOptions, maxMeta, writes });
  const words = useRoundTypeWords();
  const fine = useFinePointer();
  const [roundMenu, setRoundMenu] = useState<RoundMenuState>(null);
  const [measured, setMeasured] = useState<Record<string, number>>({});
  // `extra`: what the box around the measured content adds (its borders).
  const report = (key: string, extra: number) => (e: LayoutChangeEvent) => {
    const h = Math.ceil(e.nativeEvent.layout.height) + extra;
    setMeasured((m) => (m[key] === h ? m : { ...m, [key]: h }));
  };
  const sIdx = nav.dayIdx;
  const day = dayLabel(draft, sIdx);
  const slots = draft.sessions[sIdx]?.slots ?? [];
  const groups = groupLabels(draft, sIdx, t);
  const types = draftRoundTypes(draft);
  const rounds = Array.from({ length: draft.cycleWeeks }, (_, w) => w);
  const lensLabel: Record<DraftGridLens, string> = {
    effort: t("editorV3.grid.lensEffort", { defaultValue: "Effort" }),
    reps: t("editorV3.grid.lensReps", { defaultValue: "Reps" }),
    load: t("editorV3.grid.lensLoad", { defaultValue: "Load" }),
    sets: t("editorV3.grid.lensSets", { defaultValue: "Sets" }),
  };
  // The day's hardest band over the cycle, by the grid's own rule.
  const dayBand = useMemo(
    () =>
      buildIntensityGridFromDraft(draft, { maxes: maxMeta, cellUnit }).days[sIdx]?.band ?? null,
    [draft, maxMeta, cellUnit, sIdx],
  );
  // The rounds by hand of each exercise: the exceptions to its rule.
  const hand = slots.map((slot) => {
    const rec = ruleOfSlot(slot, types, loadStepFor(cellUnit));
    return new Set(rec.kind === "rule" ? rec.exceptions.map((e) => e.week) : []);
  });
  const typeOf = (w: number) => types[w] ?? "normal";
  // «W ＋»'s hold repeats the round last opened, as the heatmap's does.
  const repeat = days.repeatRound;
  const { weekClone } = useGridGestures({
    t,
    canRemoveWeek: false,
    canRemoveDay: false,
    onCloneWeek: repeat && (() => repeat(nav.roundIdx)),
  });
  // The height of row `id`: the tallest content among its name and its cells.
  const heightOf = (id: string, min: number) =>
    Math.max(min, measured[`${id}:n`] ?? 0, ...rounds.map((w) => measured[`${id}:${w}`] ?? 0));
  const headH = heightOf("head", HEAD_MIN);
  const ctx: ColumnCtx = {
    theme,
    slots,
    lens: nav.gridLens,
    lineOptions,
    cellUnit,
    maxMeta,
    resolveName,
    hand,
    words,
    typeOf,
    headH,
    heightOf,
    report,
    open: edit.open,
    range: edit.range,
    handleAt: edit.handleAt,
    fieldAt: edit.fieldAt,
    onCell: edit.onCell,
    cellGesture: edit.cellGesture,
    onHead: (w) => setRoundMenu({ week: w, mode: "menu" }),
  };

  return (
    <View style={styles.section}>
      <View style={styles.headRow}>
        <Text style={styles.headTitle} accessibilityRole="header" numberOfLines={1}>
          {day}
        </Text>
        {dayBand ? (
          <View
            accessible
            accessibilityLabel={t("editorV3.grid.dayPeakA11y", { band: bandLabel(t, dayBand), defaultValue: "Peak: {{band}}" })}
            style={g.peak}
          >
            <Text style={styles.muted}>{t("editorV3.grid.dayPeak", { defaultValue: "Peak" })}</Text>
            <ScaleLegend variant="bands" theme={theme} bands={[dayBand]} />
          </View>
        ) : null}
        {onDayMenu ? (
          <IconButton
            theme={theme}
            name="more"
            accessibilityLabel={t("editorV3.dayMenuA11y", { day, defaultValue: "{{day}}, day menu" })}
            onPress={onDayMenu}
          />
        ) : null}
        {slots.length > 0 ? (
          <SelectToggle
            theme={theme}
            active={edit.selecting}
            onPress={edit.toggleSelecting}
            day={day}
            a11yIdle={{ key: "editorV3.grid.selectA11y", defaultValue: "Select cells in {{day}}" }}
            a11yDone={{ key: "editorV3.grid.selectDoneA11y", defaultValue: "Done selecting cells in {{day}}" }}
          />
        ) : null}
        <Button
          theme={theme}
          variant="secondary"
          compact
          icon="plus"
          label={t("mobile.logger.addExercise", { defaultValue: "Add exercise" })}
          onPress={onAddExercise}
        />
      </View>
      <ChipChoiceRow
        theme={theme}
        choices={LENSES}
        selected={nav.gridLens}
        onSelect={nav.setGridLens}
        label={(l) => lensLabel[l]}
        chipA11yLabel={(l) => lensLabel[l]}
        groupA11yLabel={t("editorV3.grid.lensA11y", { defaultValue: "Show in the grid" })}
      />
      {slots.length === 0 ? (
        <Text style={styles.empty}>{t("editorV3.dayEmpty", { day, defaultValue: "No exercises in {{day}} yet." })}</Text>
      ) : (
        <View ref={edit.keys.rootRef} style={g.frame}>
          <View style={g.names}>
            <View style={[c.head, g.nameHead, { height: headH }]}>
              <View onLayout={report("head:n", LINE)} style={g.nameBox}>
                <Text style={g.headText}>{t("editorV3.grid.exercise", { defaultValue: "Exercise" })}</Text>
              </View>
            </View>
            {slots.map((slot) => {
              const tag = slot.group ? groups.get(slot.group.id) : undefined;
              return (
                <View key={slot.id} style={[g.row, g.nameCell, { height: heightOf(slot.id, ROW_MIN) }]}>
                  <View onLayout={report(`${slot.id}:n`, LINE)} style={g.nameBox}>
                    <Text style={g.name}>{resolveName(slot.exerciseId, slot.exerciseName)}</Text>
                    {tag ? <Text style={styles.tag}>{tag}</Text> : null}
                  </View>
                </View>
              );
            })}
          </View>
          <ScrollView horizontal style={g.rounds} contentContainerStyle={g.roundsRow}>
            {rounds.map((w) => (
              <RoundColumn key={w} ctx={ctx} w={w} styles={c} />
            ))}
            {days.addRound ? (
              <View style={[c.head, c.roundHead, g.addRound, { height: headH }]}>
                <AddRoundChip theme={theme} onPress={days.addRound} gesture={weekClone()} />
              </View>
            ) : null}
          </ScrollView>
        </View>
      )}
      {edit.rangeBar ? <GridRangeBar theme={theme} {...edit.rangeBar} /> : null}
      <ScaleLegend variant="bands" theme={theme} />
      {repeat ? (
        <Text style={styles.muted}>{t("composer.holdAddWeekToCloneHint", { defaultValue: "Hold W ＋ to repeat this week" })}</Text>
      ) : null}
      {nav.gridLens === "sets" ? (
        <Text style={styles.muted}>
          {t("editorV3.grid.setsNote", {
            defaultValue: "Type the sets of one round: the change holds for that round only, on the whole superset.",
          })}
        </Text>
      ) : null}
      <Text style={styles.muted}>
        {t("editorV3.grid.note", {
          defaultValue:
            "One lens at a time. Load shows the weight written, or ≈ the load from your MAX for a row at effort. Tap a cell to open it on the right. Only the Sets lens is written here: change reps, load and effort from the panel.",
        })}
      </Text>
      {edit.keys.hasKeys && fine ? (
        <Text style={styles.muted}>
          {t("editorV3.grid.keysNote", {
            defaultValue:
              "Keys: arrows move, Shift with an arrow or a click selects a block, Esc cancels. Enter opens the exercise; in Sets, Enter or a digit types. Ctrl+C copies and Ctrl+V pastes (Cmd on a Mac). In Sets, Ctrl+D fills down and Ctrl+R fills right, in place of the browser's own. Drag the square at the block's corner to fill.",
          })}
        </Text>
      ) : null}
      {edit.region}
      <RoundMenu theme={theme} state={roundMenu} setState={setRoundMenu} cycleWeeks={draft.cycleWeeks} typeOf={typeOf} days={days} />
    </View>
  );
}

function makeGridStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    frame: {
      flexDirection: "row",
      borderWidth: 1,
      borderColor: p.border,
      borderRadius: r.r3,
      backgroundColor: p.surface,
      overflow: "hidden",
    },
    names: { width: NAME_W, borderRightWidth: 1, borderRightColor: p.border },
    rounds: { flex: 1 },
    // Few rounds spread over the width, as the mock's table; many scroll.
    roundsRow: { flexDirection: "row", flexGrow: 1 },
    nameBox: { paddingVertical: s.s1 },
    nameHead: { paddingHorizontal: s.s3 },
    headText: { color: p.ink2, fontSize: ty.sizes.sm, fontWeight: "600" },
    // The name column's rows; the rounds' are RoundGridCell's.
    row: { minHeight: ROW_MIN, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: p.border },
    nameCell: { justifyContent: "center", paddingHorizontal: s.s3 },
    name: { color: p.ink, fontSize: ty.sizes.sm, fontWeight: "600" },
    // «W ＋» after the last round: a head with no cells under it.
    addRound: { justifyContent: "center" },
    peak: { flexDirection: "row", alignItems: "center", gap: s.s2 },
  });
}
